/**
 * RAG API Service
 *
 * API client for RAG (Retrieval-Augmented Generation) features.
 *
 * @module services/ragApi
 * @task TMF9-648
 */

import { api, authHeader } from './apiClient';
import type {
  ChatMessage,
  ChatCompletionRequest,
  ChatCompletionResponse,
  RAGSearchRequest,
  RAGSearchResponse,
  AIDocument,
  AIDocumentCreate,
  AIDocumentUpdate,
  AIDocumentListResponse,
  AIConversation,
  AIConversationListResponse,
  AIConversationMessage,
  AIUsageStats,
  IndexingResult,
} from '@/types/rag';

const BASE_URL = '/ai';

// ============================================================================
// Chat Completions
// ============================================================================

export const chatApi = {
  /**
   * Generate AI chat completion with optional RAG context.
   */
  complete: async (request: ChatCompletionRequest): Promise<ChatCompletionResponse> => {
    return api.post<ChatCompletionResponse>(`${BASE_URL}/chat`, request);
  },

  /**
   * Stream AI chat completion.
   * Returns an EventSource-like async generator.
   */
  stream: async function* (
    request: ChatCompletionRequest
  ): AsyncGenerator<string, void, unknown> {
    let response: Response;

    try {
      // Usar URL relativa para que o Vite proxy resolva (VITE_API_URL é hostname Docker, inacessivel do browser)
      response = await fetch(`/api/v1${BASE_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Erro de rede ao iniciar stream de chat:', error);
      throw new Error('Falha na conexão com o servidor de chat', { cause: error });
    }

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      console.error(`Erro HTTP no stream de chat: ${response.status}`, errorBody);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('No reader available');
    }

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') return;
            yield data;
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },
};

// ============================================================================
// RAG Search
// ============================================================================

export const searchApi = {
  /**
   * Search knowledge base using vector similarity.
   */
  search: async (request: RAGSearchRequest): Promise<RAGSearchResponse> => {
    return api.post<RAGSearchResponse>(`${BASE_URL}/search`, request);
  },
};

// ============================================================================
// Documents
// ============================================================================

export const documentApi = {
  /**
   * List all indexed documents.
   */
  list: async (params?: {
    sourceType?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AIDocumentListResponse> => {
    return api.get<AIDocumentListResponse>(`${BASE_URL}/documents`, { params });
  },

  /**
   * Get a document by ID.
   */
  getById: async (id: number): Promise<AIDocument> => {
    return api.get<AIDocument>(`${BASE_URL}/documents/${id}`);
  },

  /**
   * Create and index a new document.
   */
  create: async (document: AIDocumentCreate): Promise<AIDocument> => {
    return api.post<AIDocument>(`${BASE_URL}/documents`, document);
  },

  /**
   * Bulk create documents.
   */
  bulkCreate: async (documents: AIDocumentCreate[]): Promise<{
    created: number;
    failed: number;
    errors: Array<{ title: string; error: string }>;
  }> => {
    return api.post(`${BASE_URL}/documents/bulk`, { documents });
  },

  /**
   * Update a document and reindex it.
   */
  update: async (id: number, update: AIDocumentUpdate): Promise<AIDocument> => {
    return api.put<AIDocument>(`${BASE_URL}/documents/${id}`, update);
  },

  /**
   * Delete a document and its chunks.
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`${BASE_URL}/documents/${id}`);
  },

  /**
   * Reindex a document.
   */
  reindex: async (id: number): Promise<AIDocument> => {
    return api.post<AIDocument>(`${BASE_URL}/documents/${id}/reindex`);
  },
};

// ============================================================================
// Indexing
// ============================================================================

export const indexingApi = {
  /**
   * Index a specific conversation.
   */
  indexConversation: async (conversationId: number): Promise<AIDocument> => {
    return api.post<AIDocument>(`${BASE_URL}/index/conversation`, {
      conversationId,
    });
  },

  /**
   * Index all closed conversations not yet indexed.
   */
  indexClosedConversations: async (params?: {
    since?: string;
    limit?: number;
  }): Promise<IndexingResult> => {
    return api.post<IndexingResult>(`${BASE_URL}/index/conversations`, params);
  },

  /**
   * Index a FAQ item.
   */
  indexFaq: async (faq: {
    question: string;
    answer: string;
    faqId?: string;
    category?: string;
    tags?: string[];
  }): Promise<AIDocument> => {
    return api.post<AIDocument>(`${BASE_URL}/index/faq`, faq);
  },

  /**
   * Reindex all documents.
   */
  reindexAll: async (sourceType?: string): Promise<IndexingResult> => {
    const params = sourceType ? `?source_type=${sourceType}` : '';
    return api.post<IndexingResult>(`${BASE_URL}/index/reindex-all${params}`);
  },
};

// ============================================================================
// Conversations
// ============================================================================

export const aiConversationApi = {
  /**
   * List AI conversations.
   */
  list: async (params?: {
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AIConversationListResponse> => {
    return api.get<AIConversationListResponse>(`${BASE_URL}/conversations`, {
      params,
    });
  },

  /**
   * Get an AI conversation by ID.
   */
  getById: async (id: number): Promise<AIConversation> => {
    return api.get<AIConversation>(`${BASE_URL}/conversations/${id}`);
  },

  /**
   * Get messages from an AI conversation.
   */
  getMessages: async (conversationId: number, limit = 50): Promise<AIConversationMessage[]> => {
    return api.get<AIConversationMessage[]>(
      `${BASE_URL}/conversations/${conversationId}/messages?limit=${limit}`
    );
  },

  /**
   * Add feedback to a message.
   */
  addFeedback: async (
    conversationId: number,
    messageId: number,
    feedback: { rating: number; text?: string }
  ): Promise<void> => {
    await api.post(
      `${BASE_URL}/conversations/${conversationId}/feedback?message_id=${messageId}`,
      feedback
    );
  },
};

// ============================================================================
// Statistics
// ============================================================================

export const aiStatsApi = {
  /**
   * Get AI usage statistics.
   */
  getUsageStats: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<AIUsageStats> => {
    return api.get<AIUsageStats>(`${BASE_URL}/stats`, { params });
  },

  /**
   * Get document indexing statistics.
   */
  getDocumentStats: async (): Promise<{
    documents: {
      total: number;
      byStatus: Record<string, number>;
      bySource: Record<string, number>;
    };
    chunks: {
      total: number;
      totalTokens: number;
    };
  }> => {
    return api.get(`${BASE_URL}/stats/documents`);
  },
};

// ============================================================================
// Hub Integration
// ============================================================================

export interface HubSyncRequest {
  clientId: string;
  documentType?: string;
  limit?: number;
}

export interface HubSyncResult {
  clientId: string;
  indexed: number;
  failed: number;
  skipped: number;
  totalFound: number;
  errors: Array<{ documentId: string; error: string }>;
}

export interface HubSyncAllRequest {
  clientIds: string[];
}

export interface HubSyncAllResult {
  totalClients: number;
  totalIndexed: number;
  totalFailed: number;
  results: HubSyncResult[];
}

export interface HubDocumentsStatus {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  sourceType: string;
}

export const hubApi = {
  /**
   * Sync documents from TMF Hub for a specific client.
   */
  syncClient: async (request: HubSyncRequest): Promise<HubSyncResult> => {
    return api.post<HubSyncResult>(`${BASE_URL}/hub/sync`, {
      client_id: request.clientId,
      document_type: request.documentType,
      limit: request.limit,
    });
  },

  /**
   * Sync documents from multiple TMF Hub clients.
   */
  syncAll: async (request: HubSyncAllRequest): Promise<HubSyncAllResult> => {
    return api.post<HubSyncAllResult>(`${BASE_URL}/hub/sync-all`, {
      client_ids: request.clientIds,
    });
  },

  /**
   * Get status of indexed Hub documents.
   */
  getStatus: async (): Promise<HubDocumentsStatus> => {
    return api.get<HubDocumentsStatus>(`${BASE_URL}/hub/status`);
  },

  /**
   * Delete Hub documents from the index.
   */
  deleteDocuments: async (clientId?: string): Promise<{ deleted: number }> => {
    const params = clientId ? { client_id: clientId } : {};
    return api.delete(`${BASE_URL}/hub/documents`, { params });
  },
};

// Export all
export const ragApi = {
  chat: chatApi,
  search: searchApi,
  documents: documentApi,
  indexing: indexingApi,
  conversations: aiConversationApi,
  stats: aiStatsApi,
  hub: hubApi,
};

export default ragApi;
